import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigationType } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { api } from '../../libs/axios';
import { Bilhete, Emissor, Pedido, ViacaoAlerta } from '../../utils/interfaces';

interface UsePedidoProps {
  id: string;
  revalidateOnHistoryRestore?: boolean;
}

export type AgrupamentoServico = {
  servico: string;
  viagens: DetalhePagamento[]
}

export type DetalhePagamento = {
  bilhetes: Bilhete[]
  detalhes: {
    empresa: string
    alerta: ViacaoAlerta | null
    destino: string
    classe: string
    origem: string
    endereco_embarque?: string | null
    saida: string
    chegada: string
    devolucao_bloqueada?: boolean
    devolucao_presencial?: boolean
    aviso_devolucao_presencial?: string | null
  }
  pagamento: {
    valor_total: number
    valor_bilhete: number
    taxa_sistema: number
    taxa_agencia: number
    valor_seguro: number
    viagem_premiada?: number
  }
}

export type BilhetePagamento = {
  [contexto: string]: AgrupamentoServico[];
}

export type ResponseUsePedido = {
  pedido: Pedido;
  emissor: Emissor;
  bilhetes: BilhetePagamento
};

async function getPedido({ id }: UsePedidoProps): Promise<ResponseUsePedido> {
  const response = await api.get(`/pedido/${id}`, {
    params: {
      restore_check: Date.now(),
    },
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
      Expires: "0",
    },
  }).then((res: AxiosResponse) => {
      return res.data;
    });

  const pedido = response.pedido;
  const bilhetes = response.bilhetes;
  const emissor = response.emissor;

  return {
    pedido,
    bilhetes,
    emissor,
  };
}

const pagamentoRouteRegex = /^\/pagamento\/\d+(\/(link-webpag|pos))?$/;

export function usePedido({ id, revalidateOnHistoryRestore = false }: UsePedidoProps) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const mountedRef = useRef(true);
  const revalidationInFlightRef = useRef(false);
  const [isRestoringPedido, setIsRestoringPedido] = useState(false);
  const [pedidoValidationFailed, setPedidoValidationFailed] = useState(false);
  const [isPedidoPageVisible, setIsPedidoPageVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState === 'visible'
  );

  const query = useQuery({
    queryKey: ['pedido', { id }],
    queryFn: () => getPedido({ id }),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const revalidatePedido = useCallback(async ({ blocking = false }: { blocking?: boolean } = {}) => {
    if (!revalidateOnHistoryRestore) {
      return;
    }

    if (revalidationInFlightRef.current) {
      return;
    }

    revalidationInFlightRef.current = true;

    if (blocking) {
      setIsRestoringPedido(true);
    }

    setPedidoValidationFailed(false);

    try {
      const result = await query.refetch();
      const pedido = result.data?.pedido;
      const status = pedido?.status;
      const responsePedidoId = pedido?.id;

      if (
        result.isError ||
        !pedido ||
        typeof status === 'undefined' ||
        String(responsePedidoId) !== String(id)
      ) {
        if (mountedRef.current) {
          setPedidoValidationFailed(true);
        }
      }
    } catch {
      if (mountedRef.current) {
        setPedidoValidationFailed(true);
      }
    } finally {
      revalidationInFlightRef.current = false;

      if (blocking && mountedRef.current) {
        setIsRestoringPedido(false);
      }
    }
  }, [id, query.refetch, revalidateOnHistoryRestore]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!revalidateOnHistoryRestore || navigationType !== 'POP') {
      return;
    }

    void revalidatePedido({ blocking: !query.data });
  }, [location.key, navigationType, query.data, revalidateOnHistoryRestore, revalidatePedido]);

  useEffect(() => {
    if (!revalidateOnHistoryRestore) {
      return;
    }

    const revalidarSeRotaPagamento = () => {
      if (!pagamentoRouteRegex.test(window.location.pathname)) {
        return;
      }

      if (document.visibilityState !== 'visible' || query.isFetching) {
        return;
      }

      void revalidatePedido({ blocking: false });
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return;
      }

      const isPagamentoRoute = pagamentoRouteRegex.test(window.location.pathname);

      if (isPagamentoRoute) {
        window.location.reload();
      }
    };

    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      setIsPedidoPageVisible(isVisible);
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', revalidarSeRotaPagamento);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', revalidarSeRotaPagamento);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [query.isFetching, revalidateOnHistoryRestore, revalidatePedido]);

  return {
    ...query,
    isRestoringPedido,
    isPedidoPageVisible,
    pedidoValidationFailed,
    retryPedidoValidation: () => revalidatePedido({ blocking: !query.data }),
  };
}
